import { ShieldCheck, X } from "lucide-react";
import { paiseToInrShort } from "../../../lib/format.ts";

export interface ApprovalBannerProps {
  approvalReadyCount: number;
  heldForProofCount: number;
  totalPaise: string;
  topVendorName: string | null;
  /**
   * When provided, renders a dismiss (×) control. The caller owns the
   * dismissed state (and persists it), mirroring `CompletionStrip`'s
   * `onSkipAll` contract.
   */
  onDismiss?: () => void;
}

export function ApprovalBanner({
  approvalReadyCount,
  heldForProofCount,
  totalPaise,
  topVendorName,
  onDismiss,
}: ApprovalBannerProps) {
  const pendingCount = approvalReadyCount + heldForProofCount;
  const hasPending = pendingCount > 0;

  return (
    <section
      role="region"
      aria-label="Vendor approvals pending"
      className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50/70 p-4 shadow-sm sm:flex-row sm:items-center"
    >
      <span
        aria-hidden
        className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white text-amber-700 shadow-sm"
      >
        <ShieldCheck size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold text-ink">
          {hasPending
            ? `${pendingCount} vendor payment${pendingCount === 1 ? "" : "s"} need approval today`
            : "No vendor payments need approval today"}
        </h2>
        <p className="mt-1 text-sm text-muted">
          {hasPending ? (
            <>
              Total{" "}
              <strong className="font-semibold text-ink">{paiseToInrShort(totalPaise)}</strong>
              {topVendorName ? (
                <>
                  {" "}
                  across vendors ·{" "}
                  <strong className="font-semibold text-ink">{topVendorName}</strong> has the most
                  bills
                </>
              ) : null}
              {heldForProofCount > 0 ? ` · ${heldForProofCount} held for proof` : null}
            </>
          ) : (
            "Approved and paid bills will continue to stay visible in the table below."
          )}
        </p>
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss approvals banner"
          className="grid h-8 w-8 shrink-0 place-items-center self-start rounded-md text-amber-700/70 hover:bg-amber-100 hover:text-amber-800 sm:self-center"
        >
          <X size={16} strokeWidth={2} aria-hidden />
        </button>
      ) : null}
    </section>
  );
}
